import {
  SlideOutModal,
  ConfirmationModal,
  Icon,
  IconDefinitions,
  SearchableTubingDropdown,
  DirectionPicker,
  HandleDropDownListChange,
  HandleDropDownListBlur,
  InfoSection,
  InfoDetail,
  SectionHeader,
  AddressFormSection,
  SearchableDropdown,
  DropdownOption,
} from '../../../../components';
import { mapCountryToEnum } from '../../../../components/form/dropdown/Dropdown';
import { useState, useEffect, cloneElement, ReactElement } from 'react';
import { ListItemProps } from '@progress/kendo-react-dropdowns';
import { Button, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { AddressModel, DataFileInfo, ImportInspectionRequest, ImportIntoInspectionRequest, ImportableInspectionModel } from '../../../../data/entities';
import { showErrorMessage, showSuccessMessage } from '../../../../app/tools';
import sharedProperty, { useSharedState } from '../../../../app/sharedProperty';
import { Formik } from 'formik';
import * as yup from 'yup';
import { getLocalDateString } from '../../../../app/dateHelpers';
import CTEyeService from '../../../../services/CTEyeService';
import { CompanyService } from '../../../../services/CompanyService';
import TubingService from '../../../../services/TubingService';
import { router, routes } from '../../../../app/routes';
import './CreateInspectionModal.scss';

interface CreateInspectionModalProps {
  showState: sharedProperty<boolean>;
  clientId: string;
  file: DataFileInfo | null;
  onSuccess: () => void;
}

export function CreateInspectionModal({ 
  showState, 
  clientId, 
  file, 
  onSuccess 
}: CreateInspectionModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadingTubing, setLoadingTubing] = useState(false);
  const [companyId, setCompanyId] = useState<number | undefined>(undefined);
  const [companyAddress, setCompanyAddress] = useState<AddressModel | null>(null);
  const [addressLoaded, setAddressLoaded] = useState(false);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [importableInspections, setImportableInspections] = useState<ImportableInspectionModel[]>([]);
  const [selectedInspectionId, setSelectedInspectionId] = useState('');
  const showConfirmImportState = useSharedState(false);
  const defaultCountry = 0; // Canada
  const defaultRegion = 2; // Alberta

  const cteyeService = new CTEyeService();

  useEffect(() => {
    new CTEyeService().getClientByIdOData(clientId).then((client) => {
      if (client?.companyId) {
        setCompanyId(client.companyId);
        new CompanyService().getAddress(client.companyId).then((address) => {
          if (address) {
            setCompanyAddress(address);
          }
        }).finally(() => setAddressLoaded(true));
      }else {
      setAddressLoaded(true);
    }
    });
  }, [clientId]);

  useEffect(() => {
    if (!showState.value) return;
    setMode('new');
    setSelectedInspectionId('');
    new CTEyeService().getImportableInspections(clientId).then((list) => {
      setImportableInspections(list ?? []);
    });
  }, [showState.value, clientId]);

  const schema = yup.object({
    tubingId: yup.number().required('Tubing is required'),
    tubingLength: yup.number().typeError('Length must be a number').moreThan(0, 'Must be greater than 0').required('Inspection length is required'),
    inspectionDate: yup.date().required('Inspection date is required'),
    isReversed: yup.boolean(),
    streetAddress: yup.string().required('Street address is required').matches(/^[a-z0-9 ,.'#-]*$/i, 'Invalid street address'),
    streetAddress2: yup.string(),
    city: yup.string().required('City is required'),
    country: yup.number().required('Country is required'),
    provinceId: yup.string().required('Province is required'),
    postalCode: yup.string().required('Postal code is required').matches(/^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$|^\d{5}(-\d{4})?$/, 'Invalid postal code'),
  });

  const handleCancel = () => {
    showState.setter(false);
  };

  if (!file || companyId === undefined || !addressLoaded) return null;

  const selectedInspection = importableInspections
    .find((i) => String(i.inspectionId) === selectedInspectionId) ?? null;

  const inspectionOptions: DropdownOption[] = importableInspections.map((i) => ({
    value: String(i.inspectionId),
    text: `${i.reference} — ${i.tubingName}`,
  }));

  const renderInspectionItem = (li: ReactElement<any>, itemProps: ListItemProps) => {
    const inspection = importableInspections
      .find((i) => String(i.inspectionId) === itemProps.dataItem.value);
    if (!inspection) return li;
    return cloneElement(
      li,
      li.props,
      <div>
        <div>{inspection.reference}</div>
        <small className="text-muted d-block">
          {new Date(inspection.inspectionDate).toLocaleDateString()} · {inspection.tubingName}
        </small>
      </div>
    );
  };

  const renderInspectionValue = (element: ReactElement<any>, value: any) => {
    const inspection = importableInspections
      .find((i) => String(i.inspectionId) === value?.value);
    if (!inspection) return element;
    return cloneElement(element, element.props, inspection.reference);
  };

  const handleImportIntoExisting = async () => {
    if (!selectedInspection) return;
    setLoading(true);
    try {
      const request: ImportIntoInspectionRequest = {
        clientId: clientId,
        filePath: file.path,
        fileName: file.fileName ?? '',
      };

      const result = await cteyeService.importIntoInspection(
        clientId,
        selectedInspection.inspectionId,
        request
      );

      if (!result) {
        showErrorMessage('Failed to import data into the inspection. Please try again.');
        return;
      }

      showSuccessMessage(
        `Data import started for inspection ${selectedInspection.reference}. Existing data is being replaced.`
      );

      showState.setter(false);
      onSuccess();

      router.navigateWithParams(
        routes.cteyeClientUploadSessions,
        new Map([["id", clientId]])
      );
    } catch (error) {
      console.error('Error importing into existing inspection:', error);
      showErrorMessage('An error occurred while importing into the inspection');
    } finally {
      setLoading(false);
    }
  };

  const initialInspectionDate = new Date(file.fileDate ?? file.lastWriteTime);

  const fileDetails: InfoDetail[] = [
    {
      label: "File Name",
      value: <code className="small">{file.fileName}</code>,
      icon: <Icon iconName={IconDefinitions.fileAlt} />,
    },
    ...(file.sizeBytes ? [{
      label: "Size",
      value: `${(file.sizeBytes / 1024 / 1024).toFixed(2)} MB`,
      icon: <Icon iconName={IconDefinitions.database} />,
    }] : []),
    {
      label: "File Date",
      value: new Date(initialInspectionDate).toLocaleDateString(),
      icon: <Icon iconName={IconDefinitions.calendar} />,
    },
  ];

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        tubingId: '',
        tubingLength: '' as number | '',
        inspectionDate: getLocalDateString(initialInspectionDate),
        isReversed: false,
        streetAddress: companyAddress?.streetAddress ?? '',
        streetAddress2: companyAddress?.streetAddress2 ?? '',
        city: companyAddress?.city ?? '',
        country: companyAddress?.province
          ? mapCountryToEnum(companyAddress.province.country).toString()
          : defaultCountry.toString(),
        provinceId: companyAddress?.provinceId?.toString() ?? defaultRegion.toString(),
        postalCode: companyAddress?.postalCode ?? '',
      }}
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting }) => {
        setLoading(true);
        try {
          const request: ImportInspectionRequest = {
            clientId: clientId,
            tubingId: parseInt(values.tubingId),
            tubingLength: values.tubingLength !== '' ? values.tubingLength as number : undefined,
            inspectionDate: new Date(values.inspectionDate).toISOString(),
            isReversed: values.isReversed,
            address: {
              streetAddress: values.streetAddress,
              streetAddress2: values.streetAddress2,
              city: values.city,
              provinceId: parseInt(values.provinceId),
              postalCode: values.postalCode,
            },
            filePath: file.path,
            fileName: file.fileName ?? '',
          };

          const result = await cteyeService.importInspection(clientId, request);

          if (!result) {
            showErrorMessage('Failed to import inspection. Please try again.');
            return;
          }

          showSuccessMessage(
            `Inspection created successfully (ID: ${result.inspectionId}). Processing started for ${file.fileName}.`
          );

          showState.setter(false);
          onSuccess();

          router.navigateWithParams(
            routes.cteyeClientUploadSessions,
            new Map([["id", clientId]])
          );

        } catch (error) {
          console.error('Error importing inspection:', error);
          showErrorMessage('An error occurred while importing the inspection');
        } finally {
          setLoading(false);
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, values, errors, touched, setFieldValue, isSubmitting }) => (
        <>
        <SlideOutModal
          showState={showState}
          title={mode === 'new'
            ? "Create Inspection from Data File"
            : "Import Data File into Existing Inspection"}
          footer={
            <div className="row m-0 w-100">
              <div className="col-6 ps-0">
                <Button
                  onClick={handleCancel}
                  variant="cancel"
                  className="w-100"
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
              <div className="col-6 pe-0">
                {mode === 'new' ? (
                  <Button
                    onClick={() => handleSubmit()}
                    variant="primary"
                    className="w-100"
                    disabled={loading || isSubmitting}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Icon iconName={IconDefinitions.plus} className="me-2" />
                        Create Inspection
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => showConfirmImportState.setter(true)}
                    variant="primary"
                    className="w-100"
                    disabled={loading || !selectedInspection}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Importing...
                      </>
                    ) : (
                      'Import Data'
                    )}
                  </Button>
                )}
              </div>
            </div>
          }
        >
          <Form noValidate onSubmit={handleSubmit}>
            {/* File Information */}
            <InfoSection title="Data File Information" details={fileDetails} />

            {/* Import Target */}
            <div className="btn-group w-100 mb-3 import-mode-toggle">
              <Button
                variant={mode === 'new' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setMode('new')}
              >
                New Inspection
              </Button>
              <Button
                variant={mode === 'existing' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setMode('existing')}
              >
                Existing Inspection
              </Button>
            </div>

            {/* Existing Inspection Target */}
            {mode === 'existing' && (
              <div className="mb-3">
                <SectionHeader title="Target Inspection" />
                <div className="px-3 pt-3">
                  <SearchableDropdown
                    label="Inspection"
                    name="targetInspection"
                    options={inspectionOptions}
                    defaultItem={{ text: 'Select an inspection...', value: '' }}
                    value={inspectionOptions.find((o) => o.value === selectedInspectionId)}
                    itemRender={renderInspectionItem}
                    valueRender={renderInspectionValue}
                    onSelect={(option) => setSelectedInspectionId(option?.value ?? '')}
                  />
                  {selectedInspection && (
                    <div className="alert alert-warning mb-0">
                      <div className="d-flex align-items-center fw-semibold mb-2">
                        <Icon
                          iconName={IconDefinitions.triangleExclamation}
                          className="me-2"
                        />
                        Warning
                      </div>
                      <ul className="mb-0 ps-3">
                        <li>
                          Importing will permanently erase this inspection's existing
                          data points and peak signals
                          {selectedInspection.isPublished
                            ? ', and its published report will be unpublished'
                            : ''}.
                        </li>
                        {selectedInspection.noteCount > 0 && (
                          <li>
                            {selectedInspection.noteCount} note(s) will be kept, but
                            their positions may no longer match the new data.
                          </li>
                        )}
                        {!selectedInspection.hasData && (
                          <li>This inspection currently has no imported data.</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Inspection Details */}
            {mode === 'new' && (
            <div className="mb-3">
              <SectionHeader title="Inspection Details" />
              <div className="px-3 pt-3">
                {/* Tubing Dropdown */}
                <Row className="mb-3">
                  <Col lg={12}>
                    <Form.Group controlId="tubingId">
                      <SearchableTubingDropdown
                        name="tubingId"
                        value={values.tubingId}
                        companyId={companyId}
                        onChange={async (e) => {
                          HandleDropDownListChange(e, setFieldValue);
                          const selectedId = (e as any).value?.value;
                          if (selectedId) {
                            setLoadingTubing(true);
                            try {
                              const tubing = await new TubingService().getTubingDetails(selectedId);
                              setFieldValue('tubingLength', tubing.length ?? '');
                            } catch {
                            } finally {
                              setLoadingTubing(false);
                            }
                          } else {
                            setFieldValue('tubingLength', '');
                          }
                        }}
                        onBlur={(e) => HandleDropDownListBlur(e, setFieldValue)}
                        isInvalid={touched.tubingId && !!errors.tubingId}
                      >
                        <Form.Control.Feedback type="invalid">
                          {errors.tubingId}
                        </Form.Control.Feedback>
                      </SearchableTubingDropdown>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Inspection Length */}
                <Row className="mb-3">
                  <Col lg={12}>
                    <Form.Group controlId="tubingLength">
                      <FloatingLabel
                        className="h-100 pb-0 floating-label-fixed"
                        label="Inspection length"
                        style={{ color: "rgba(var(--bs-body-color-rgb), 0.65)" }}
                      >
                        <Form.Control
                          type="number"
                          name="tubingLength"
                          value={values.tubingLength}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.tubingLength && !!errors.tubingLength}
                          disabled={loadingTubing}
                          placeholder="Inspection length"
                        />
                        {loadingTubing && (
                          <span
                            className="spinner-border spinner-border-sm text-secondary position-absolute"
                            style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }}
                          />
                        )}
                        <Form.Control.Feedback type="invalid">
                          {errors.tubingLength}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Inspection Date */}
                <Row className="mb-3">
                  <Col lg={12}>
                    <Form.Group controlId="inspectionDate">
                      <FloatingLabel
                        className="h-100 pb-0 floating-label-fixed"
                        label="Inspection date"
                        style={{ color: "rgba(var(--bs-body-color-rgb), 0.65)" }}
                      >
                        <Form.Control
                          type="date"
                          name="inspectionDate"
                          value={values.inspectionDate?.slice(0, 10)}
                          onChange={(e) =>
                            setFieldValue("inspectionDate", e.target.value)
                          }
                          onBlur={handleBlur}
                          isInvalid={touched.inspectionDate && !!errors.inspectionDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.inspectionDate}
                        </Form.Control.Feedback>
                      </FloatingLabel>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Direction Picker */}
                <Row className="mb-3">
                  <Col lg={12}>
                    <FloatingLabel
                      controlId="cmbDirection"
                      label="Direction"
                      className="mb-0"
                      aria-label="Direction"
                      title="Direction"
                    >
                      <DirectionPicker
                        id="cmbDirection"
                        label="Direction"
                        initialValue={values.isReversed.toString()}
                        onSelect={(value: string) => {
                          setFieldValue("isReversed", value === "true");
                        }}
                      />
                    </FloatingLabel>
                  </Col>
                </Row>
              </div>
            </div>
            )}

            {/* Address Section */}
            {mode === 'new' && (
            <AddressFormSection
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              defaultCountry={defaultCountry}
              defaultRegion={defaultRegion}
            />
            )}
          </Form>
        </SlideOutModal>
        <ConfirmationModal
          showState={showConfirmImportState}
          title="Replace Inspection Data?"
          confirmText="Wipe and Import"
          onConfirm={handleImportIntoExisting}
        >
          {selectedInspection && (
            <>
              <InfoSection
                title="Import Details"
                details={[
                  {
                    label: 'Inspection',
                    value: selectedInspection.reference,
                    icon: <Icon iconName={IconDefinitions.clipboard} />,
                  },
                  {
                    label: 'Data File',
                    value: file.fileName ?? '',
                    icon: <Icon iconName={IconDefinitions.file} />,
                  },
                ]}
              />
              <div className="alert alert-warning mb-0">
                <div className="d-flex align-items-center fw-semibold mb-2">
                  <Icon
                    iconName={IconDefinitions.triangleExclamation}
                    className="me-2"
                  />
                  This cannot be undone
                </div>
                <ul className="mb-0 ps-3">
                  <li>
                    All existing data points and peak signals on this inspection
                    will be permanently erased and replaced with the data from{' '}
                    {file.fileName}.
                  </li>
                  {selectedInspection.isPublished && (
                    <li>The published report will be unpublished.</li>
                  )}
                  {selectedInspection.noteCount > 0 && (
                    <li>
                      {selectedInspection.noteCount} note(s) will be kept, but
                      their positions may no longer match the new data.
                    </li>
                  )}
                </ul>
              </div>
            </>
          )}
        </ConfirmationModal>
        </>
      )}
    </Formik>
  );
}

export default CreateInspectionModal;
